import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  coverageCodeList,
  MEMBER_STATUS,
  QueryKeys,
  ResponseCode,
} from "@/constants";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import type { PlanCoverageInfo, Relation } from "@/model/demographics";
import { queryClient } from "./use-query-client";
import type { LoginParams } from "@/services/user";
import {
  demographics,
  globalFlags,
  login,
  providerNetworkType,
} from "@/services/user";
import { useNotificationError } from "@/context/error-provider";
import { useDispatch, useSelector } from "react-redux";
import {
  setActiveCoverage,
  setCoveragePlanDates,
  setSelectedCoverageYear,
} from "@/store/active-coverage-slice";
import type { IPwdExpiryResponse } from "@/model/user";
import { fetchCoverageDates } from "@/services/spend-summary";
import type { RootState } from "@/store/store";
import { isCoverageActiveOrExtended } from "@/lib/utils";

const useLogin = () => {
  const { addError } = useNotificationError();

  return useMutation({
    mutationKey: [QueryKeys.LOGIN],
    mutationFn: (params: LoginParams) => login(params),
    onError: (error) => {
      const axiosError = (error as AxiosError).response;
      const errorResponse = axiosError?.data as IPwdExpiryResponse;
      // console.log("error axios >>", axiosError);

      if (axiosError) {
        switch (axiosError?.status) {
          case ResponseCode.UNAUTHORIZED:
            addError({
              message: errorResponse?.message,
              type: ErrorNotificationType.ERROR,
            });
            break;
          case ResponseCode.FORBIDDEN:
            addError({
              message: errorResponse?.message,
              type: ErrorNotificationType.ERROR,
            });
            break;
          case ResponseCode.SERVER_ERROR:
            addError({
              message: "Server error",
              type: ErrorNotificationType.ERROR,
            });
            break;
          case ResponseCode.BAD_REQUEST:
            addError({
              message: "Bad request",
              type: ErrorNotificationType.ERROR,
            });
            break;
          default:
            addError({
              message: "An error occurred",
              type: ErrorNotificationType.ERROR,
            });
            break;
        }
      } else {
        addError({
          message: error.message ?? "An error occurred",
          type: ErrorNotificationType.ERROR,
        });
      }
    },
    // onSuccess: () => {
    //   addError({
    //     message: "Logged In Successfully",
    //     type: ErrorNotificationType.INFO,
    //   });
    // },
  });
};

// const useDemographics = (
//   userInfo: any,
// ): UseQueryResult<Demographics, Error> => {
//   return useQuery<Demographics, Error>({
//     queryKey: [QueryKeys.DEMOGRAPHICS, userInfo.username],
//     queryFn: async () => {
//       const data = await demographics(userInfo);
//       const normalizedMember: Record<string, any> = {};
//       if (data.relations) {
//         const distinctDependents: Relation[] = Array.from(
//           new Set(data.relations),
//         );
//         normalizedMember[data.memberId] = {
//           lastName: data.lastName,
//           zipCode: data.zipCode,
//           planDesc: data.planDesc,
//           gender: data.gender,
//           city: data.city,
//           groupId: data.groupId,
//           hipaaProtected: data.hipaaProtected,
//           employeeId: data.employeeId,
//           intMbrId: data.intMbrId,
//           firstName: data.firstName,
//           phoneNumber: data.phoneNumber,
//           mbrId: data.mbrId,
//           dob: data.dob,
//           middleName: data.middleName,
//           state: data.state,
//           relationship: data.relationship,
//           age: data.age,
//           email: data.email,
//           memberId: data.memberId,
//           username: data.username,
//           status: data.status,
//         };
//         distinctDependents.forEach((relation: Relation) => {
//           normalizedMember[relation.memberId] = {
//             lastName: relation.lastName,
//             zipCode: relation.zipCode,
//             planDesc: relation.planDesc,
//             gender: relation.gender,
//             city: relation.city,
//             groupId: relation.groupId,
//             hipaaProtected: relation.hipaaProtected,
//             employeeId: relation.employeeId,
//             intMbrId: relation.intMbrId,
//             firstName: relation.firstName,
//             phoneNumber: relation.phoneNumber,
//             mbrId: relation.mbrId,
//             dob: relation.dob,
//             middleName: relation.middleName,
//             state: relation.state,
//             relationship: relation.relationship,
//             age: relation.age,
//             email: relation.email,
//             memberId: relation.memberId,
//             username: relation.username,
//             status: relation.status,
//           };
//         });

//         queryClient.setQueryData([QueryKeys.MEMBERS], normalizedMember);
//         return data;
//       }
//     },
//     enabled: !!userInfo.username && !!userInfo.accessToken,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     gcTime: 10 * 60 * 1000, // 10 minutes
//   });
// };

// const useGlobalFlags = (userInfo: any): UseQueryResult<GlobalFlag, Error> => {
//   console.log("global flags", userInfo);
//   return useQuery<GlobalFlag, Error>({
//     queryKey: [QueryKeys.GLOBAL_FLAGS, userInfo.username],
//     queryFn: () => globalFlags(userInfo),
//     select(data: any) {
//       return data.data[0];
//     },
//     enabled:
//       !!userInfo.username &&
//       !!userInfo.accessToken &&
//       !!userInfo.memberId &&
//       !!userInfo.groupId,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     gcTime: 10 * 60 * 1000, // 10 minutes
//   });
// };

// const useProviderNetwork = (
//   userInfo: any,
// ): UseQueryResult<ProviderNetwork, Error> => {
//   console.log("provider network", userInfo);
//   return useQuery<ProviderNetwork, Error>({
//     queryKey: [QueryKeys.PROVIDER_NETWORK_TYPE, userInfo.username],
//     queryFn: async () => {
//       const data = await providerNetworkType(userInfo);
//       return data;
//     },
//     enabled:
//       !!userInfo.username &&
//       !!userInfo.accessToken &&
//       !!userInfo.memberId &&
//       !!userInfo.groupId,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     gcTime: 10 * 60 * 1000, // 10 minutes
//   });
// };

// Alternative approach using useQueries for dependent queries

const useUserDataQueries = (initialUserInfo: {
  groupId: string;
  divisionId: string;
  accessToken: string;
  username: string;
  memberId?: string;
}) => {
  const dispatch = useDispatch();

  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  // First, get demographics
  const demographicsQuery = useQuery({
    queryKey: [QueryKeys.DEMOGRAPHICS, initialUserInfo.username],
    queryFn: async () => {
      const data = await demographics(initialUserInfo);
      const normalizedMember: Record<string, unknown> = {};
      normalizedMember[data.memberId] = {
        lastName: data.lastName,
        zipCode: data.zipCode,
        planDesc: data.planDesc,
        gender: data.gender,
        city: data.city,
        groupId: data.groupId,
        hipaaProtected: data.hipaaProtected,
        employeeId: data.employeeId,
        intMbrId: data.intMbrId,
        firstName: data.firstName,
        phoneNumber: data.phoneNumber,
        mbrId: data.mbrId,
        dob: data.dob,
        middleName: data.middleName,
        state: data.state,
        relationship: data.relationship,
        age: data.age,
        email: data.email,
        memberId: data.memberId,
        username: data.username,
        status: data.status,
        planCoverageInfo: data.planCoverageInfo ?? [],
      };

      const orderMap = new Map(
        coverageCodeList.map((code, index) => [code, index]),
      );

      const sortedCoverage = data.planCoverageInfo
        .sort(
          (a, b) =>
            orderMap.get(a.claimCoverageCode)! -
            orderMap.get(b.claimCoverageCode)!,
        )
        .find((coverage: PlanCoverageInfo) =>
          isCoverageActiveOrExtended(coverage),
        );
      // console.log("orderedCoverage", sortedCoverage);

      dispatch(setActiveCoverage(sortedCoverage ?? data?.planCoverageInfo[0]));

      if (data.relations) {
        const distinctDependents: Relation[] = Array.from(
          new Set(data.relations),
        );

        distinctDependents.forEach((relation: Relation) => {
          normalizedMember[relation.memberId] = {
            lastName: relation.lastName,
            zipCode: relation.zipCode,
            planDesc: relation.planDesc,
            gender: relation.gender,
            city: relation.city,
            groupId: relation.groupId,
            hipaaProtected: relation.hipaaProtected,
            employeeId: relation.employeeId,
            intMbrId: relation.intMbrId,
            firstName: relation.firstName,
            phoneNumber: relation.phoneNumber,
            mbrId: relation.mbrId,
            dob: relation.dob,
            middleName: relation.middleName,
            state: relation.state,
            relationship: relation.relationship,
            age: relation.age,
            email: relation.email,
            memberId: relation.memberId,
            username: relation.username,
            status: relation.status,
            planCoverageInfo: relation.planCoverageInfo ?? [],
          };
        });
        queryClient.setQueryData(
          [QueryKeys.DEPENDENTS],
          distinctDependents.filter((d) => {
            if (d?.status === MEMBER_STATUS?.ACTIVE) {
              return true;
            }
            const planInExtendedPeriod = d?.planCoverageInfo?.find(
              (plan: PlanCoverageInfo) => isCoverageActiveOrExtended(plan),
            );
            return Boolean(planInExtendedPeriod);
          }),
        );

        queryClient.setQueryData(
          [QueryKeys.MEMBERS],
          Object.fromEntries(
            Object.entries(normalizedMember).filter(([_, v]) => {
              if (v?.status === MEMBER_STATUS?.ACTIVE) {
                return true;
              }
              const planInExtendedPeriod = v?.planCoverageInfo?.find(
                (plan: PlanCoverageInfo) => isCoverageActiveOrExtended(plan),
              );
              return Boolean(planInExtendedPeriod);
            }),
          ),
        );
        /**
         * NOTE:
         * The value for `MEMBERS` is also stored under a separate key `MEMBERS_LOOKUP`.
         * This redundant caching is intentional.
         *
         * Background:
         * Originally, `MEMBERS` was used for modules specific usage and lookup purpose.
         * During a recent change for Court Order Dependents, value of MEMBERS was manipulated
         * so introducing the new key `MEMBERS_LOOKUP`.
         *
         * Reason:
         * - `MEMBERS` continues to serve its original role in its existing usage in certain modules.
         * - `MEMBERS_LOOKUP` exists solely to support quick lookup.
         *
         */
        queryClient.setQueryData(
          [QueryKeys.MEMBERS_LOOKUP],
          Object.fromEntries(
            Object.entries(normalizedMember).filter(([_, v]) => {
              if (v?.status === MEMBER_STATUS?.ACTIVE) {
                return true;
              }
              const planInExtendedPeriod = v?.planCoverageInfo?.find(
                (plan: PlanCoverageInfo) => isCoverageActiveOrExtended(plan),
              );
              return Boolean(planInExtendedPeriod);
            }),
          ),
        );
        return data;
      }
    },
    enabled:
      Boolean(initialUserInfo.username) &&
      Boolean(initialUserInfo.accessToken) &&
      Boolean(initialUserInfo.groupId) &&
      Boolean(initialUserInfo.divisionId),
  });

  // Then get dependent queries
  const dependentQueries = useQueries({
    queries: [
      // Global Flags (dependent on demographics)
      {
        queryKey: [QueryKeys.GLOBAL_FLAGS, initialUserInfo.username],
        queryFn: () =>
          globalFlags({
            ...initialUserInfo,
            memberId: demographicsQuery.data?.memberId,
            groupId: demographicsQuery.data?.groupId,
            divisionId: demographicsQuery.data?.divisionId,
          }),
        select: (data: { data?: Array<Record<string, unknown>> }) =>
          data.data?.[0],
        enabled:
          Boolean(initialUserInfo.username) &&
          Boolean(initialUserInfo.accessToken) &&
          Boolean(demographicsQuery.data?.memberId) &&
          Boolean(demographicsQuery.data?.groupId),
      },
      // Provider Network (dependent on demographics)
      {
        queryKey: [QueryKeys.PROVIDER_NETWORK_TYPE, initialUserInfo.username],
        queryFn: () =>
          providerNetworkType({
            ...initialUserInfo,
            memberId: demographicsQuery.data?.memberId,
            groupId: demographicsQuery.data?.groupId,
            divisionId: demographicsQuery.data?.divisionId,
            planId: demographicsQuery.data?.planId,
          }),
        enabled:
          Boolean(initialUserInfo.username) &&
          Boolean(initialUserInfo.accessToken) &&
          Boolean(demographicsQuery.data?.memberId) &&
          Boolean(demographicsQuery.data?.groupId) &&
          Boolean(demographicsQuery.data?.planId),
      },
      // Coverage Years (dependent on demographics)
      {
        queryKey: [QueryKeys.COVERAGE_YEARS, initialUserInfo.username],
        queryFn: async () => {
          const data = await fetchCoverageDates({
            accessToken: initialUserInfo.accessToken,
            employeeId: demographicsQuery.data?.employeeId,
            groupId: demographicsQuery.data?.groupId,
            divisionId: demographicsQuery.data?.divisionId,
            planId: demographicsQuery.data?.planId,
          });

          const tempCoveragePlanDates = data.coverages.map((item) => ({
            ...item,
            id: crypto.randomUUID(),
            periods: item.periods.map((period) => ({
              ...period,
              claimCoverageCode: item.type,
              id: crypto.randomUUID(),
            })),
          }));

          dispatch(setCoveragePlanDates(tempCoveragePlanDates));

          const selectedCoveragePlanDate = tempCoveragePlanDates.find(
            (coverage) => coverage.type === activeCoverage.claimCoverageCode,
          )?.periods[0];
          dispatch(setSelectedCoverageYear(selectedCoveragePlanDate ?? null));

          return data;
        },
        retry: false,
        retryOnMount: false,
        enabled:
          Boolean(initialUserInfo.username) &&
          Boolean(initialUserInfo.accessToken) &&
          Boolean(demographicsQuery.data?.memberId) &&
          Boolean(demographicsQuery.data?.groupId),
      },
    ],
  });

  return {
    demographicsQuery,
    globalFlagsQuery: dependentQueries[0],
    providerNetworkQuery: dependentQueries[1],
    coverageYearsQuery: dependentQueries[2],
  };
};

export { useLogin, useUserDataQueries };
